import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { formatDisplayDate } from '../utils/formatDate';

const SubscriptionControlLayer = () => {
    const { auth } = useAuth();
    const [subscriptions, setSubscriptions] = useState([]);
    const [kpiStats, setKpiStats] = useState({
        active_paid: 0,
        free_subs: 0,
        rev_month: 0,
        rev_year: 0
    });
    const [loading, setLoading] = useState(false);

    // Filters
    const [searchVenue, setSearchVenue] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPlan, setFilterPlan] = useState('all');

    const fetchSubscriptions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/all-subscriptions`);
            if (response.ok) {
                const data = await response.json();
                const list = data.subscriptions || [];
                setSubscriptions(list);

                if (data.kpi) {
                    setKpiStats(data.kpi);
                } else {
                    // Fallback client computation
                    const activePaid = list.filter(s => s.status === 'active' && !(s.plan || '').toLowerCase().includes('free')).length;
                    const freeCount = list.filter(s => s.status === 'active' && (s.plan || '').toLowerCase().includes('free')).length;
                    const totalRev = list.reduce((acc, s) => acc + (s.revenue || 0), 0);
                    setKpiStats({
                        active_paid: activePaid,
                        free_subs: freeCount,
                        rev_month: totalRev * 0.35, // estimate
                        rev_year: totalRev
                    });
                }
            }
        } catch (error) {
            console.error('Failed to fetch subscriptions', error);
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchSubscriptions();
    }, [fetchSubscriptions]);

    const filteredSubscriptions = useMemo(() => {
        return subscriptions.filter(sub => {
            if (searchVenue.trim()) {
                const q = searchVenue.toLowerCase();
                const name = (sub.venueName || '').toLowerCase();
                const email = (sub.email || '').toLowerCase();
                const stripe = (sub.stripeId || '').toLowerCase();
                if (!name.includes(q) && !email.includes(q) && !stripe.includes(q)) {
                    return false;
                }
            }

            if (filterStatus !== 'all') {
                const st = (sub.status || '').toLowerCase();
                if (filterStatus === 'active' && st !== 'active') return false;
                if (filterStatus === 'cancelled' && !['canceled', 'cancelled'].includes(st)) return false;
                if (filterStatus === 'expired' && !['past_due', 'unpaid', 'expired', 'incomplete_expired'].includes(st)) return false;
            }

            if (filterPlan !== 'all') {
                if (!(sub.plan || '').toLowerCase().includes(filterPlan.toLowerCase())) return false;
            }

            return true;
        });
    }, [subscriptions, searchVenue, filterStatus, filterPlan]);

    const getStatusBadge = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'active') {
            return <span className="hs-badge hs-badge-success">Active</span>;
        }
        if (['canceled', 'cancelled'].includes(s)) {
            return <span className="hs-badge hs-badge-danger">Cancelled</span>;
        }
        if (['past_due', 'unpaid', 'expired'].includes(s)) {
            return <span className="hs-badge hs-badge-warning">Expired</span>;
        }
        return <span className="hs-badge hs-badge-neutral">{status || 'Unknown'}</span>;
    };

    const getPlanBadge = (planName) => {
        const p = (planName || '').toLowerCase();
        if (p.includes('premium')) return <span className="badge bg-primary-50 text-primary-600 border border-primary-200 px-3 py-1 radius-pill fw-semibold">{planName}</span>;
        if (p.includes('basic')) return <span className="badge bg-info-50 text-info-600 border border-info-200 px-3 py-1 radius-pill fw-semibold">{planName}</span>;
        if (p.includes('free')) return <span className="badge bg-success-50 text-success-600 border border-success-200 px-3 py-1 radius-pill fw-semibold">{planName}</span>;
        return <span className="badge bg-neutral-100 text-secondary-light border px-3 py-1 radius-pill fw-semibold">{planName || 'Standard'}</span>;
    };

    return (
        <div className="d-flex flex-column gap-20">
            {/* 4 Summary KPI Cards at Top (Static) */}
            <div className="row g-3">
                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-primary)' }}>
                        <div className="hs-stat-icon-wrapper bg-primary-50 text-primary-600">
                            <Icon icon="solar:crown-line-duotone" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Active Paid Subscriptions</span>
                            <h4 className="fw-bold mb-0 text-dark">{kpiStats.active_paid}</h4>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-success)' }}>
                        <div className="hs-stat-icon-wrapper bg-success-50 text-success-600">
                            <Icon icon="solar:shop-2-bold" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Free Subscriptions</span>
                            <h4 className="fw-bold mb-0 text-dark">{kpiStats.free_subs}</h4>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-info)' }}>
                        <div className="hs-stat-icon-wrapper bg-info-50 text-info-600">
                            <Icon icon="solar:calendar-date-bold" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Subscription Revenue This Month</span>
                            <h4 className="fw-bold mb-0 text-dark">{kpiStats.rev_month.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} AED</h4>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                    <div className="hs-stat-card h-100" style={{ '--stat-accent': 'var(--hs-warning)' }}>
                        <div className="hs-stat-icon-wrapper bg-warning-50 text-warning-600">
                            <Icon icon="solar:chart-2-bold" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-xs fw-semibold text-uppercase d-block mb-1">Subscription Revenue This Year</span>
                            <h4 className="fw-bold mb-0 text-dark">{kpiStats.rev_year.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} AED</h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* Subscriptions Table Card */}
            <div className="hs-card p-0">
                <div className="card-header border-bottom bg-white py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div>
                        <h6 className="text-lg fw-bold mb-0 text-dark">Venue Subscriptions</h6>
                        <span className="text-xs text-secondary-light">Overview of subscribed venues, billing cycles, and revenue contributions</span>
                    </div>

                    <div className="d-flex align-items-center flex-wrap gap-2">
                        {/* Search Venue */}
                        <div className="hs-search-wrapper" style={{ width: '200px', flexShrink: 0 }}>
                            <Icon icon="solar:magnifer-linear" className="hs-search-icon" />
                            <input
                                type="text"
                                className="hs-input"
                                placeholder="Search venue..."
                                value={searchVenue}
                                onChange={(e) => setSearchVenue(e.target.value)}
                            />
                        </div>

                        {/* Status Filter */}
                        <select 
                            className="hs-select text-xs" 
                            style={{ width: '140px', flexShrink: 0 }}
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="expired">Expired</option>
                        </select>

                        {/* Plan Filter */}
                        <select 
                            className="hs-select text-xs" 
                            style={{ width: '140px', flexShrink: 0 }}
                            value={filterPlan}
                            onChange={(e) => setFilterPlan(e.target.value)}
                        >
                            <option value="all">All Plans</option>
                            <option value="free">Free</option>
                            <option value="basic">Basic</option>
                            <option value="premium">Premium</option>
                        </select>

                        <button 
                            className="hs-btn-secondary hs-btn-sm d-flex align-items-center gap-2"
                            onClick={fetchSubscriptions}
                            title="Refresh Subscriptions"
                        >
                            <Icon icon="solar:refresh-bold" />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="hs-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">Venue Name</th>
                                    <th scope="col">Current Plan</th>
                                    <th scope="col">Subscription Status</th>
                                    <th scope="col">Renewal Date</th>
                                    <th scope="col" className="text-end">Total Revenue (AED)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="text-center py-5 text-secondary-light">Loading subscriptions...</td></tr>
                                ) : filteredSubscriptions.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center py-5 text-secondary-light">No subscriptions found matching the selected filters.</td></tr>
                                ) : (
                                    filteredSubscriptions.map(sub => (
                                        <tr key={sub.id}>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="fw-bold text-dark text-sm">{sub.venueName}</span>
                                                    {sub.stripeId && (
                                                        <span className="text-xs text-secondary-light">Stripe: {sub.stripeId}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{getPlanBadge(sub.plan)}</td>
                                            <td>{getStatusBadge(sub.status)}</td>
                                            <td className="text-secondary-light text-sm">
                                                {sub.renewalDate ? formatDisplayDate(sub.renewalDate) : 'Lifetime / Open'}
                                            </td>
                                            <td className="text-end">
                                                <span className="fw-bold text-dark text-sm">
                                                    {(sub.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionControlLayer;
